import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Search } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import api from "@/lib/api";

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("All");
  const [query, setQuery] = useState("");

  useEffect(() => {
    api.get("/blog/posts")
      .then((r) => setPosts(r.data.posts || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => ["All", ...new Set(posts.map((p) => p.category))], [posts]);
  const visible = posts.filter((p) => {
    const catOk = category === "All" || p.category === category;
    const q = query.trim().toLowerCase();
    const qOk = !q || p.title.toLowerCase().includes(q) || p.excerpt.toLowerCase().includes(q);
    return catOk && qOk;
  });

  return (
    <main data-testid="blog-page">
      <Seo
        title="HR Briefings for UK SMEs, Employment Law & Practice | Electra HR"
        siteName="Electra HR"
        description="Plain-English briefings on UK employment law, HR processes and small-business people problems. Written by a practising solicitor."
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            { "@type": "Blog", name: "Electra HR Briefings" },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: window.location.origin },
                { "@type": "ListItem", position: 2, name: "Briefings", item: `${window.location.origin}/blog` },
              ],
            },
          ],
        }}
      />
      <section className="grain bg-ink pb-16 pt-32 sm:pt-40 text-paper">
        <div className="container-x">
          <p className="eyebrow text-pink">Briefings</p>
          <h1 className="mt-5 max-w-3xl font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]">
            HR thinking, minus the waffle.
          </h1>
          <p className="mt-6 max-w-2xl text-base sm:text-lg leading-relaxed text-paper/65">
            Short, honest guides from the studio, written for founders in the middle of it.
          </p>
        </div>
      </section>

      <section className="bg-paper py-16 sm:py-24">
        <div className="container-x">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
            <div className="flex flex-wrap gap-3">
              {categories.map((c) => (
                <button
                  key={c}
                  data-testid={`blog-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => setCategory(c)}
                  className={`border-2 px-4 py-2 font-mono text-xs uppercase tracking-[0.15em] transition-colors ${
                    category === c ? "border-ink bg-ink text-paper" : "border-ink/20 text-ink/60 hover:border-ink"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
            <div className="relative sm:ml-auto">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink/40" />
              <input
                data-testid="blog-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search briefings"
                aria-label="Search briefings"
                className="w-full sm:w-64 border-2 border-ink/20 bg-transparent py-2.5 pl-9 pr-3 text-sm outline-none focus:border-pink placeholder:text-ink/35"
              />
            </div>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {loading && [0, 1, 2].map((i) => <div key={i} className="h-64 animate-pulse border-2 border-ink/10 bg-ink/[0.03]" />)}
            {!loading && visible.length === 0 && (
              <p data-testid="blog-empty-state" className="col-span-full py-16 text-center text-ink/50">
                Nothing matches that. Try another word, or ask us directly.
              </p>
            )}
            {visible.map((p, i) => (
              <Reveal key={p.slug} delay={(i % 3) * 0.08}>
                <Link
                  to={`/blog/${p.slug}`}
                  data-testid={`blog-card-${p.slug}`}
                  className="group flex h-full flex-col border-2 border-ink bg-paper p-7 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[8px_8px_0_0_#0A0E17]"
                >
                  <div className="flex items-center justify-between font-mono text-xs uppercase tracking-[0.15em]">
                    <span className="text-ink/50">{p.category}</span>
                    <span className="text-ink/35">{p.read_time} min</span>
                  </div>
                  <h2 className="mt-4 font-display text-xl font-semibold leading-snug tracking-tight">{p.title}</h2>
                  <p className="mt-3 text-sm leading-relaxed text-ink/60 line-clamp-3">{p.excerpt}</p>
                  <span className="mt-auto pt-6 flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-ink/45 group-hover:text-ink">
                    Read <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
